import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { getWeeklyTrends } from '../../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useChartColors } from '../../hooks/useDarkMode';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function TrendChart() {
  const colors = useChartColors();
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTrends();
  }, []);

  const loadTrends = async () => {
    try {
      const data = await getWeeklyTrends(12);
      setTrends(data.reverse()); // Reverse to show oldest first
    } catch (error) {
      console.error('Error loading trends:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <p>Lade Trends...</p>;
  }

  if (trends.length === 0) {
    return <p>Keine Trend-Daten verfügbar. Führe zuerst das Trend-Calculation-Script aus.</p>;
  }

  const labels = trends.map(t => {
    const date = new Date(t.week_start_date);
    return `KW ${date.getDate()}.${date.getMonth() + 1}`;
  });

  const expectedLoss = trends.map(t => t.expected_weight_loss_kg ? -parseFloat(t.expected_weight_loss_kg) : null);
  const actualLoss = trends.map(t => t.actual_weight_loss_kg ? -parseFloat(t.actual_weight_loss_kg) : null);

  const data = {
    labels,
    datasets: [
      {
        label: 'Erwarteter Gewichtsverlust',
        data: expectedLoss,
        borderColor: colors.primary.border,
        backgroundColor: colors.primary.background,
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 4,
        tension: 0.1,
      },
      {
        label: 'Tatsächlicher Gewichtsverlust',
        data: actualLoss,
        borderColor: colors.success.border,
        backgroundColor: colors.success.background,
        borderWidth: 2,
        pointRadius: 4,
        tension: 0.1,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: colors.textColor,
          usePointStyle: true,
          padding: 20,
        }
      },
      tooltip: {
        backgroundColor: colors.tooltip.background,
        titleColor: colors.tooltip.text,
        bodyColor: colors.tooltip.text,
        borderColor: colors.tooltip.border,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value ? value.toFixed(2) : 'N/A'} kg`;
          }
        }
      }
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Gewichtsänderung (kg)',
          color: colors.textMuted,
        },
        ticks: {
          color: colors.textMuted,
        },
        grid: {
          color: (context) => {
            if (context.tick.value === 0) {
              return colors.gridZeroLine;
            }
            return colors.gridColor;
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Kalenderwoche',
          color: colors.textMuted,
        },
        ticks: {
          color: colors.textMuted,
        },
        grid: {
          color: colors.gridColor,
        }
      }
    }
  };

  return (
    <div style={{ height: '350px' }}>
      <Line data={data} options={options} />
    </div>
  );
}

export default TrendChart;
